import { faInfo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { useCompanyDateFormat } from "../../../utils/hooks/company";
import { UncontrolledTooltip } from "reactstrap";
import { renderSettingsResourceLabel } from "../../../utils/helpers/settings";
import { PendingAdjustmentBadge } from "./PendingAdjustment";

const columnHelper = createColumnHelper();

export const useColumns = ({
    edit,
    remove,
    clone,
    transfer,
    ledger,
    payout,
    isOpenFromTimesheet = false,
    open,
    reviewAdjustment,
    scheduled,
}) => {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();

    return useMemo(() => {
        const columns = [
            columnHelper.accessor("plan", {
                header: t("plan"),
                cell: (info) => {
                    const value = info.getValue();
                    if (!value) {
                        return null;
                    }
                    // a transfer of this plan scheduled for a later date (listed above the table with Cancel)
                    const transfer = (scheduled || []).find((change) => change.mode === "transfer" && change.sourceUserTimeOffPlan === info.row.original.id);
                    if (!transfer) {
                        return renderSettingsResourceLabel(value);
                    }
                    return <>
                        <div>{renderSettingsResourceLabel(value)}</div>
                        <span
                            className={`badge ${transfer.status === "failed" || transfer.blockedReasons?.length ? "badge-danger" : "badge-info"}`}
                            title={transfer.blockedReasons?.join("; ") || transfer.failedReason || ""}
                        >
                            {t("scheduled-transfer-badge", { plan: transfer.planCode, date: moment(transfer.effectiveDate).format(dateFormat) })}
                        </span>
                    </>;
                },
            }),
            columnHelper.accessor("bucket.initial", {
                header: t("initial-balance"),
                cell: (info) => {
                    const value = info.getValue();
                    if (typeof value === "undefined" || Number.isNaN(value)) {
                        return null;
                    }
                    return Number(value).toFixed(2);
                },
            }),

            columnHelper.accessor("bucket.updates", {
                header: t("added"),
                cell: (info) => {
                    const value = info.getValue();
                    if (typeof value === "undefined" || Number.isNaN(value)) {
                        return null;
                    }
                    return Number(value).toFixed(2);
                },
            }),
            // Accrued
            columnHelper.accessor("bucket.earned", {
                header: t("accrued"),
                cell: (info) => {
                    const value = info.getValue();
                    if (typeof value === "undefined" || Number.isNaN(value)) {
                        return null;
                    }
                    return Number(value).toFixed(2);
                },
            }),
            // Pending
            columnHelper.accessor("bucket.pending", {
                header: t("pending"),
                cell: (info) => {
                    const value = info.getValue();
                    if (typeof value === "undefined" || Number.isNaN(value)) {
                        return null;
                    }
                    const pending = Number(value).toFixed(2);
                    const data = info.row.original;
                    if(!Array.isArray(data.pendingItems) || data.pendingItems.length === 0 ) {
                        return pending;
                    }
                    const id = `pending-bucket-${data.id}`;
                    return <>
                        <span>{pending}</span>
                        
                        <span id={id} className="text-muted text-sm ml-2">
                            <FontAwesomeIcon
                                className="text-primary cursor-pointer px-2  py-1"
                                icon={faInfo}
                                onClick={() => open(data.pendingItems)}
                            />
                        </span>
                    </>
                },
            }),
            //Taken
            columnHelper.accessor("bucket.consumed", {
                header: t("taken"),
                cell: (info) => {
                    const value = info.getValue();
                    if (typeof value === "undefined" || Number.isNaN(value)) {
                        return null;
                    }
                    return Number(value).toFixed(2);
                },
            }),
            // Available Balance
            columnHelper.accessor("bucket.available", {
                header: t("available-balance"),
                cell: (info) => {
                    const value = info.getValue();
                    if (typeof value === "undefined" || Number.isNaN(value)) {
                        return null;
                    }
                    const { id, pendingAdjustments } = info.row.original;
                    if (!Array.isArray(pendingAdjustments) || !pendingAdjustments.length) {
                        return Number(value).toFixed(2);
                    }
                    // changes waiting for an admin (a pay period before a transfer, a back-dated plan
                    // version): a marker each, the details window explains and approves / rejects
                    return <>
                        <div>{Number(value).toFixed(2)}</div>
                        <div className="d-flex flex-wrap gap-1">
                            {pendingAdjustments.map((adjustment) => (
                                <PendingAdjustmentBadge
                                    key={adjustment.id}
                                    adjustment={adjustment}
                                    onClick={reviewAdjustment && !isOpenFromTimesheet ? () => reviewAdjustment(id, adjustment.id) : undefined}
                                />
                            ))}
                        </div>
                    </>;
                },
            }),
        ];
        if (!isOpenFromTimesheet) {
            const actionColumn = columnHelper.display({
                id: "actions",
                enableHiding: false,
                enableColumnFilter: false,
                header: (
                    <div className="text-right">
                        {t("actions")}
                    </div>
                ),
                cell: (info) => {
                    const { id, bucket } = info.row.original;
                    const transferId = `timeoff-transfer-${id}`;
                    const editId = `timeoff-edit-${id}`;
                    const deleteId = `timeoff-delete-${id}`;
                    const payoutId = `timeoff-payout-${id}`;
                    const cloneId = `timeoff-clone-${id}`;
                    const pendingHours = info.row.original.pendingItems
                        .filter((item) => item.type !== 'payout')
                        .reduce((acc, item) => acc + item.total, 0);
                    const isPayoutDisabled = Number(bucket?.available || 0) === 0 && pendingHours === 0;
                    return (
                        <div className="actions text-primary text-right">
                            {ledger && (
                                <span
                                    className="btn-inner--icon mr-3 icon-sm cursor-pointer"
                                    onClick={() => ledger(id)}
                                    title={t("ledger")}
                                >
                                    <i className="fa-solid fa-list-ul" />
                                </span>
                            )}
                            {payout && (
                                <>
                                    <span
                                        id={payoutId}
                                        className={`btn-inner-icon mr-3 icon-sm ${isPayoutDisabled ? "text-muted" : "cursor-pointer"}`}
                                        style={isPayoutDisabled ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
                                        onClick={() => !isPayoutDisabled && payout(id)}
                                    >
                                        <i className="fa-solid fa-money-bill" />
                                    </span>
                                    <UncontrolledTooltip target={payoutId} placement="top">
                                        {t("payout")}
                                    </UncontrolledTooltip>
                                </>
                            )}
                            {transfer && (
                                <>
                                    <span
                                        id={transferId}
                                        className="btn-inner-icon mr-3 icon-sm cursor-pointer"
                                        onClick={() => transfer(id)}
                                    >
                                        <i className="fa-solid fa-right-left" />
                                    </span>
                                    <UncontrolledTooltip target={transferId} placement="top">
                                        {t("transfer")}
                                    </UncontrolledTooltip>
                                </>
                            )}
                            {edit && (
                                <>
                                    <span
                                        id={editId}
                                        className="btn-inner--icon mr-3 icon-sm cursor-pointer"
                                        onClick={() => edit(id)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <i className="fas fa-pen" />
                                    </span>
                                    <UncontrolledTooltip target={editId} placement="top">
                                        {t("edit")}
                                    </UncontrolledTooltip>
                                </>
                            )}

                            {remove && (
                                <>
                                    <span
                                        id={deleteId}
                                        className="btn-inner--icon mr-3 icon-sm cursor-pointer"
                                        onClick={() => remove(id)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <i className="fas fa-trash" />
                                    </span>
                                    <UncontrolledTooltip target={deleteId} placement="top">
                                        {t("delete")}
                                    </UncontrolledTooltip>
                                </>
                            )}

                            {clone && (
                                <>
                                    <span
                                        id={cloneId}
                                        className="btn-inner--icon icon-sm cursor-pointer"
                                        onClick={() => clone(id)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <i className="ni ni-single-copy-04" />
                                    </span>
                                    <UncontrolledTooltip target={cloneId} placement="top">
                                        {t("clone")}
                                    </UncontrolledTooltip>
                                </>
                            )}
                        </div>
                    );
                },
            });
            columns.push(actionColumn);
        }
        return columns;
    }, [remove, edit, clone, transfer, payout, ledger, isOpenFromTimesheet, t, open, reviewAdjustment, scheduled, dateFormat]);
};
